<script setup lang="ts">
// Renders standings groups (conference/division/single-table) using the
// per-sport column set the backend supplies. Team rows link to the Team page;
// `highlight` marks/serves the current team on a Team page.
interface Entry {
  provider_team_id: string; rank: number; team: string; abbreviation: string
  logo: string; note: string; cells: Record<string, string>
}
interface Group { name: string; columns: string[]; entries: Entry[] }
const props = defineProps<{ groups: Group[]; league: string; highlight?: string }>()
const meRows = ref<Record<string, HTMLElement | null>>({})

// On a Team page, scroll the highlighted row to the middle of the container.
onMounted(() => {
  if (!props.highlight) return
  nextTick(() => {
    const el = meRows.value[props.highlight as string]
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'center', behavior: 'auto' })
    }
  })
})
function teamLink(e: Entry) {
  return `/scoreboard/team/${encodeURIComponent(e.provider_team_id)}?league=${encodeURIComponent(props.league)}`
}
</script>

<template>
  <div class="standings">
    <div v-for="g in groups" :key="g.name || 'all'" class="group">
      <h3 v-if="g.name" class="grp">{{ g.name }}</h3>
      <div class="scroll">
        <table>
          <thead>
            <tr>
              <th class="num">#</th>
              <th class="team">Team</th>
              <th v-for="c in g.columns" :key="c" class="stat">{{ c }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="e in g.entries" :key="e.provider_team_id || e.team"
              :ref="el => { if (e.provider_team_id === highlight) meRows[e.provider_team_id] = (el as HTMLElement) }"
              :class="{ me: e.provider_team_id && e.provider_team_id === highlight }">
              <td class="num">{{ e.rank }}</td>
              <td class="team">
                <NuxtLink :to="teamLink(e)" class="tlink">
                  <img v-if="e.logo" :src="e.logo" class="crest" alt="" loading="lazy" />
                  <span class="tname">{{ e.team }}</span>
                  <span v-if="e.note" class="note" :title="e.note">●</span>
                </NuxtLink>
              </td>
              <td v-for="c in g.columns" :key="c" class="stat mono">{{ e.cells[c] ?? '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <p v-if="!groups.length" class="empty">Standings aren't available for this league yet.</p>
  </div>
</template>

<style scoped>
.group { margin-bottom: 22px; }
.grp { font-size: 14px; font-weight: 800; margin-bottom: 10px; color: var(--ink); }
.scroll { overflow-x: auto; border: 1px solid var(--line); border-radius: 12px; background: var(--panel); }
table { width: 100%; border-collapse: collapse; font-size: 13px; }
thead th { text-align: right; padding: 10px 10px; font-size: 11px; text-transform: uppercase;
  letter-spacing: 0.06em; color: var(--muted); font-weight: 700; border-bottom: 1px solid var(--line); white-space: nowrap; }
thead th.num { text-align: center; width: 34px; }
thead th.team { text-align: left; }
tbody td { padding: 9px 10px; text-align: right; border-bottom: 1px solid rgba(53,42,96,0.5); white-space: nowrap; }
tbody tr:last-child td { border-bottom: none; }
tbody tr:hover { background: rgba(34,26,73,0.55); }
tbody tr.me { background: rgba(47,210,198,0.12); }
tbody tr.me td { border-color: rgba(47,210,198,0.3); }
td.num { text-align: center; color: var(--muted); font-family: var(--font-mono); }
td.team { text-align: left; }
.tlink { display: inline-flex; align-items: center; gap: 8px; }
.tlink:hover .tname { color: var(--accent); }
.crest { width: 20px; height: 20px; object-fit: contain; }
.tname { font-weight: 600; }
.note { color: var(--accent); font-size: 9px; }
.stat { font-variant-numeric: tabular-nums; }
.empty { color: var(--muted); text-align: center; padding: 28px 16px; }
</style>
